import BodyText from 'LuxKit/Typography/BodyText'
import StructureLabel from 'LuxKit/Label/StructureLabel'
import Group from 'components/utils/Group'
import { mediaQueryOnly, mediaQueryUp } from 'components/utils/breakpoint'
import getLoyaltyTierDisplayName from 'loyalty/selectors/getLoyaltyTierDisplayName'
import { getThemeClassName } from 'lib/theme/themeUtils'
import { tierGradientBackgroundStyle, tierNormalContrastColorStyle } from 'styles/utils/tierStyles'
import { rem } from 'polished'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

/*
 * The gradient surface. Layout (mobile row → tablet column, alignment, gaps) is handled by the
 * Group props below. A uniform min-height keeps the current-tier card (with its badge) level
 * with the others.
 */
const Card = styled(Group)`
  position: relative;
  border-radius: ${props => props.theme.borderRadius.L};
  padding: ${rem(20)} ${rem(24)};
  min-height: ${rem(80)};
  ${tierGradientBackgroundStyle}
  ${tierNormalContrastColorStyle}

  ${mediaQueryUp.tablet} {
    min-height: ${rem(120)};
  }
`

const TierName = styled(BodyText)`
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const Requirement = styled.div`
  text-align: end;

  ${mediaQueryOnly.mobile} {
    max-width: ${rem(160)};
  }

  ${mediaQueryUp.tablet} {
    text-align: center;
  }
`

/** Overlaps the top edge of the card on tablet+; sits inline under the tier name on mobile. */
const CurrentTierBadge = styled.div`
  /*
   * Keep the badge copy on a single line across locales. Without this the absolutely-positioned
   * badge (below) shrink-to-fits within half the card width and the label wraps to two lines.
   */
  > * {
    white-space: nowrap;
  }

  ${mediaQueryUp.tablet} {
    --current-tier-badge-inline-translate: -50%;

    position: absolute;
    top: 0;
    inset-inline-start: 50%;
    transform: translate(var(--current-tier-badge-inline-translate), -50%);

    [dir='rtl'] & {
      --current-tier-badge-inline-translate: 50%;
    }
  }
`

interface Props {
  tier: App.LoyaltyTier
  threshold: App.LoyaltyTierThreshold
  isCurrent: boolean
}

function LoyaltyTierRequirementCard({ tier, threshold, isCurrent }: Props) {
  const intl = useIntl()
  const tierName = getLoyaltyTierDisplayName(tier)
  const hasRequirement = threshold.statusCredits > 0 || threshold.nights > 0

  return (
    <Card
      className={`tier-${tier}`}
      direction="horizontal"
      tabletDirection="vertical"
      horizontalAlign="space-between"
      verticalAlign="center"
      tabletHorizontalAlign="center"
      gap={16}
      tabletGap={4}
    >
      <Group direction="vertical" gap={2} horizontalAlign="start" tabletHorizontalAlign="center">
        <TierName variant="large" weight="bold" colour="inherit">{tierName}</TierName>
        {isCurrent && <CurrentTierBadge>
          <StructureLabel variant="standard" className={getThemeClassName('default')}>
            {intl.formatMessage({
              defaultMessage: 'Your current tier',
              id: 'xJuz42geJiKMZ-fMON4n',
              description: 'Badge shown on the member’s current tier in the tier requirement cards',
            })}
          </StructureLabel>
        </CurrentTierBadge>}
      </Group>
      <Requirement>
        {hasRequirement && <>
          <BodyText variant="small" colour="inherit">
            {intl.formatMessage({
              defaultMessage: '{statusCredits, number} Status Credits',
              id: '7Dc03WOrfe1G4endgeUN',
              description: 'Status Credits required to reach a given loyalty tier, shown in the tier cards',
            }, { statusCredits: threshold.statusCredits })}
          </BodyText>
          <BodyText variant="small" colour="inherit">
            {intl.formatMessage({
              defaultMessage: '{nights, number} Nights',
              id: 'lAvNQ2GBla8XK7uuwz42',
              description: 'Number of nights required to reach a given loyalty tier, shown in the tier cards',
            }, { nights: threshold.nights })}
          </BodyText>
        </>}
        {!hasRequirement && <BodyText variant="small" colour="inherit">
          {intl.formatMessage({
            defaultMessage: 'No minimum Status Credits or Nights required',
            id: 'gdeb8tIulElYH-M9DKYn',
            description: 'Requirement copy for the entry-level (Bronze) loyalty tier',
          })}
        </BodyText>}
      </Requirement>
    </Card>
  )
}

export default LoyaltyTierRequirementCard
